import { ChatInput } from "@/components/Chat/input/ChatInput";
import { ErrorAssistantMessage } from "@/components/Chat/messages/components/ErrorAssistantMessage";
import { ProviderAvatar } from "@/components/Chat/messages/components/ProviderAvatar";
import { Markdown } from "@/components/Chat/messages/markdown";
import { Button } from "@/components/ui";
import { AuthOverlayDialog } from "@/components/ui/AuthOverlayDialog";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { SignInCard } from "@/components/ui/SignInCard";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SidebarDndProvider } from "@/contexts/SidebarDndProvider";
import { TempChatProvider, useTempChat } from "@/contexts/TempChatContext";
import { ThreadMoveProvider } from "@/contexts/ThreadMoveContext";
import { AppSidebar } from "@/features/sidebar/components/app-sidebar";
import { useCopyToClipboard } from "@/hooks/common/useCopyToClipboard";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store";
import { Check, Copy, MessageSquare, Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TempChatAssistantSelector } from "./components/TempChatAssistantSelector";
import { TempChatModelSelector } from "./components/TempChatModelSelector";

/**
 * Small pulsing circle loader component
 */
const PulsingCircle = () => {
  return (
    <span className="inline-flex items-center ml-1">
      <span className="w-2 h-2 bg-white rounded-full animate-pulse opacity-70 shadow-sm"></span>
    </span>
  );
};

/**
 * Copy button for assistant messages
 */
const CopyMessageButton = ({ content, isStreaming }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  if (isStreaming) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              copyToClipboard(content || "");
            }}
            className="h-8 w-8 rounded-md bg-transparent border-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group"
          >
            {isCopied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4 text-muted-foreground group-hover:text-sidebar-accent-foreground transition-colors" />
            )}
            <span className="sr-only">
              {isCopied ? "Copied" : "Copy message"}
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{isCopied ? "Copied!" : "Copy message"}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Inner component that uses the temp chat context
 */
function TempChat2Content() {
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);

  const user = useAuthStore((state) => state.user);
  const [showSignInCard, setShowSignInCard] = useState(false);
  const [hasUserDismissedSignIn, setHasUserDismissedSignIn] = useState(false);
  const [showAuthOverlay, setShowAuthOverlay] = useState(false);
  const [authOverlayFeature, setAuthOverlayFeature] = useState("");

  const {
    messages,
    isStreaming,
    sendMessage,
    clearChat,
    retryLastMessage,
    hasMessages,
  } = useTempChat();

  // Clear temp chat store when navigating away from temp chat page
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearChat();
    };

    const handlePopState = () => {
      clearChat();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      clearChat();
    };
  }, [clearChat]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Handle initial message from landing page
  useEffect(() => {
    const initialData = location.state;
    if (initialData?.initialMessage && !hasMessages) {
      const messageContent =
        typeof initialData.initialMessage === "string"
          ? initialData.initialMessage
          : String(initialData.initialMessage || "");

      if (messageContent.trim()) {
        sendMessage(messageContent);
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, sendMessage, hasMessages, navigate, location.pathname]);

  // Show sign in card after user has exchanged a few messages
  useEffect(() => {
    const userMessageCount = messages.filter((m) => m.role === "user").length;
    if (
      !user &&
      userMessageCount >= 4 &&
      !showSignInCard &&
      !hasUserDismissedSignIn
    ) {
      setShowSignInCard(true);
    }
  }, [user, messages, showSignInCard, hasUserDismissedSignIn]);

  // Hide sign in card when user logs in
  useEffect(() => {
    if (user && showSignInCard) {
      setShowSignInCard(false);
      setHasUserDismissedSignIn(false);
    }
  }, [user, showSignInCard]);

  const handleSendMessage = async ({ content }) => {
    if (!content?.trim()) return;
    await sendMessage(content);
  };

  const handleShowAuthOverlay = useCallback((featureName) => {
    setAuthOverlayFeature(featureName);
    setShowAuthOverlay(true);
  }, []);

  const handleRetry = useCallback(() => {
    retryLastMessage();
  }, [retryLastMessage]);

  return (
    <SidebarProvider className="z-[250] !min-h-0 h-svh overflow-hidden">
      <ThreadMoveProvider>
        <SidebarDndProvider>
          <AppSidebar tempChat />
          <SidebarInset className="transition-all duration-200 md:!m-0 md:!rounded-none md:!shadow-none overflow-hidden">
            <div className="flex flex-col h-full w-full overflow-hidden bg-transparent">
              {/* Top bar with sidebar trigger + model/assistant selectors */}
              <header
                className={cn(
                  "flex shrink-0 items-center",
                  "bg-transparent w-full",
                  "h-12 sm:h-14 md:h-16",
                  "z-[150]",
                  "transition-all duration-200"
                )}
              >
                <div className="flex items-center w-full h-full pl-3 sm:pl-4 pr-2 gap-2">
                  <SidebarTrigger className="h-8 w-8 md:hidden" />
                  <TempChatModelSelector />
                  <div className="ml-auto">
                    <TempChatAssistantSelector />
                  </div>
                </div>
              </header>

              {/* Messages Area */}
              <main
                ref={containerRef}
                className="flex-1 overflow-y-auto overflow-x-hidden pb-32"
              >
                <div className="flex-1 flex justify-center w-full">
                  <div className="w-full max-w-3xl mx-auto px-4 pt-2">
                    {!hasMessages && !isStreaming && (
                      <div className="text-center py-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                          <MessageSquare className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-semibold mb-2">
                          Welcome to Temporary Chat
                        </h2>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                          Try out our AI assistant with a quick conversation. No
                          account required, but messages won&apos;t be saved.
                        </p>
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <Sparkles className="w-4 h-4" />
                          <span>Powered by GPT-4.1 Mini</span>
                        </div>
                      </div>
                    )}

                    {hasMessages && (
                      <>
                        {messages.map((message, index) => {
                          const isUser = message.role === "user";
                          const isLast = index === messages.length - 1;

                          return (
                            <div
                              key={message.id || index}
                              className="flex flex-col w-full"
                            >
                              <div
                                className={cn(
                                  isUser ? "flex justify-end" : "",
                                  "xl:max-w-3xl 2xl:max-w-4xl",
                                  !isUser && "mb-1"
                                )}
                              >
                                <div
                                  className={cn(
                                    "flex w-full",
                                    "max-w-3xl rounded-[calc(0.75rem-1px)]",
                                    {
                                      "bg-bolt-elements-messages-background": true,
                                    }
                                  )}
                                >
                                  <div className="w-full">
                                    <div
                                      className={cn(
                                        "message-container w-full",
                                        isUser && "pb-8"
                                      )}
                                    >
                                      <div
                                        className={cn(
                                          isUser ? "flex justify-end" : "",
                                          "xl:max-w-3xl 2xl:max-w-4xl",
                                          !isUser && "mb-1"
                                        )}
                                      >
                                        <div
                                          className={cn(
                                            "relative",
                                            isUser
                                              ? "bg-muted/50 max-w-[85%] px-3 py-1 sm:px-4 sm:py-1.5 md:px-4 md:py-2 rounded-3xl"
                                              : "bg-background w-full max-w-2xl px-3 py-4 sm:px-4 sm:py-5 md:px-4 md:py-6 rounded-2xl"
                                          )}
                                        >
                                          <div className={cn("space-y-1")}>
                                            <div
                                              className={cn(
                                                "space-y-3",
                                                isUser ? "" : "text-left"
                                              )}
                                            >
                                              {isUser ? (
                                                <div className="whitespace-pre-wrap break-words text-sidebar-foreground">
                                                  {message.content}
                                                </div>
                                              ) : message.isError ? (
                                                <ErrorAssistantMessage
                                                  message={message}
                                                  onRetry={handleRetry}
                                                  isRetrying={isStreaming}
                                                  showRetry={isLast}
                                                />
                                              ) : (
                                                <div className="relative flex">
                                                  <ProviderAvatar
                                                    message={message}
                                                    isStreaming={
                                                      isStreaming && isLast
                                                    }
                                                    hasContent={
                                                      !!message.content
                                                    }
                                                  />
                                                  <div className="flex-1">
                                                    <Markdown
                                                      html
                                                      isStreaming={
                                                        isStreaming && isLast
                                                      }
                                                      darkMode={true}
                                                      className="message-content"
                                                    >
                                                      {message.content || ""}
                                                    </Markdown>
                                                    {isStreaming && isLast && (
                                                      <PulsingCircle />
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          {/* Copy button for assistant messages */}
                                          {!isUser && !message.isError && (
                                            <div className="flex items-center gap-1.5 mt-1 ml-1">
                                              <CopyMessageButton
                                                content={message.content}
                                                isStreaming={
                                                  isStreaming && isLast
                                                }
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        <div ref={messagesEndRef} className="h-px w-full" />
                      </>
                    )}
                  </div>
                </div>
              </main>

              {/* Input Area - overlaid at bottom */}
              <div className="flex-shrink-0 w-full z-[60] flex justify-center relative bg-background min-h-24 pb-6">
                <div className="w-full max-w-3xl mx-auto px-4 z-[60] -mt-6">
                  <ChatInput
                    onSendMessage={handleSendMessage}
                    placeholder="Type a message to try out our AI assistant..."
                    isTempChat={true}
                    disableTools={true}
                    disableFileUpload={true}
                    isLimitedMode={true}
                    disableDeepResearch={true}
                    maxFiles={0}
                    onShowAuthOverlay={handleShowAuthOverlay}
                  />
                </div>
              </div>
            </div>
          </SidebarInset>
        </SidebarDndProvider>
      </ThreadMoveProvider>

      {/* Sign In Card */}
      <SignInCard
        isVisible={showSignInCard}
        onClose={() => {
          setShowSignInCard(false);
          setHasUserDismissedSignIn(true);
        }}
      />

      {/* Auth Overlay Dialog */}
      <AuthOverlayDialog
        isOpen={showAuthOverlay}
        onClose={() => setShowAuthOverlay(false)}
        featureName={authOverlayFeature}
      />
    </SidebarProvider>
  );
}

/**
 * TempChat2Page - Temp chat with full AppSidebar layout
 */
export function TempChat2Page() {
  return (
    <TempChatProvider>
      <TempChat2Content />
    </TempChatProvider>
  );
}

export default TempChat2Page;
